import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { useCampaignsShow } from '@jobilla/nexus-sdk'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdditionalQuestions } from '../../../hooks/editor/useAdditionalQuestions'
import { DragHandle } from '../form-simulator/drag-handle'
import { AdditionalQuestionRow } from './additional-question-row'

export type AdditionalQuestionsEditorProps = {
	campaignId: string
}

export function AdditionalQuestionsEditor({ campaignId }: AdditionalQuestionsEditorProps) {
	const { t } = useTranslation('editor')
	const campaign = useCampaignsShow(campaignId)
	const formId = campaign.data?.form?.id

	const { questions, isLoading, isSaving, create, update, updateLocal, remove, reorder } = useAdditionalQuestions({
		formId,
	})

	const handleDragEnd = useCallback((result: DropResult) => {
		if (!result.destination || result.source.index === result.destination.index) { return }

		void reorder(result.source.index, result.destination.index)
	}, [reorder])

	const handleAddQuestion = useCallback(() => {
		void create({
			label: t('additionalQuestions.newQuestion'),
			type: 'select',
			position: questions.length,
			options: [{
				id: null,
				label: t('additionalQuestions.defaultOption'),
				position: 0,
				isDisqualifying: false,
				scoreValue: null,
				consentType: null,
			}],
		})
	}, [create, questions.length, t])

	if (!formId || isLoading) {
		return null
	}

	return (
		<div>
			{/* Purple hint banner */}
			<div
				className={'flex items-start bg-purple-50 border border-purple-200 text-purple-700 rounded p-4 mb-6 max-w-[500px]'}
			>
				<div className="flex-shrink-0">
					<svg
						className="h-5 w-5 text-purple-400"
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
						fill="currentColor"
						aria-hidden="true"
					>
						<path
							fillRule="evenodd"
							d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 10-2 0v4a1 1 0 002 0V6zm-1 8a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
							clipRule="evenodd"
						/>
					</svg>
				</div>
				<div className="ml-3">
					<p className="text-sm">
						{t('additionalQuestions.hint')}
					</p>
				</div>
			</div>

			{questions.length > 0 && (
				<div className="ring ring-grey-200 bg-white rounded-sm shadow-lg p-6" style={{ width: '500px' }}>
					{/*@TODO: Make this properly for all simulators. This one makes a layout shift as well when it activates*/}
					{/*<div className="">*/}
					{/*	{isSaving && <span className="text-xs text-grey-400 animate-pulse">Saving...</span>}*/}
					{/*</div>*/}
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId="additional-questions">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{questions.map((question, index) => (
										<Draggable key={question.id} draggableId={question.id} index={index}>
											{(provided) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													className="relative group/question"
												>
													<AdditionalQuestionRow
														question={question}
														index={index}
														onUpdate={update}
														onUpdateLocal={updateLocal}
														onDelete={remove}
														dragHandle={
															<div
																className="absolute -left-6 top-[1rem] -translate-y-1/2 text-transparent group-hover/question:text-grey-400 flex items-center justify-center"
																{...provided.dragHandleProps}
															>
																<DragHandle />
															</div>
														}
													/>
												</div>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
				</div>
			)}

			<button
				onClick={handleAddQuestion}
				className="mt-4 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 px-4 py-2 rounded cursor-pointer transition-colors"
			>
				+ {t('additionalQuestions.addQuestion')}
			</button>
		</div>
	)
}
